import React, { useState, useEffect } from "react";

import { Button, Typography } from "@mui/material";

import styles from "./Results.module.scss";

import { ReactComponent as FailedIcon } from "@/assets/svg/failed.svg";
import { ReactComponent as PassedIcon } from "@/assets/svg/passed.svg";
import ResultsViewModal from "@/pages/SecurityAwareness/components/SecurityTopic/components/ResultsViewModal";
import { mapping, MIN_PASSING_SCORE } from "@/constants/common";
import { Answer, SecurityTopicEnum } from "@/pages/SecurityAwareness/types";
import { saveTest } from "@/firebase";
import { auth } from "@/firebase";

interface ResultsProps {
  results: Array<string>;
  onResetResults: () => void;
  handleNextTopic?: () => void;
  quizName: SecurityTopicEnum;
  setTestResults?: React.Dispatch<
    React.SetStateAction<
      Map<string, { score_percent: number; completed_at: string; id: string }>
    >
  >;
}

const Results: React.FC<ResultsProps> = ({
  results,
  onResetResults,
  handleNextTopic,
  quizName,
  setTestResults,
}) => {
  const [open, setOpen] = useState(false);

  const score = results.length
    ? (results.filter(result => result === Answer.Correct).length /
        results.length) *
      100
    : 0;

  const isSuccess = score >= MIN_PASSING_SCORE;

  // Save test result on successful completion
  useEffect(() => {
    if (isSuccess && auth.currentUser && setTestResults) {
      const saveTestResult = async () => {
        try {
          const user = auth.currentUser;
          if (!user || !user.uid) {
            console.warn("User not authenticated");
            return;
          }

          const userId = user.uid;
          const email = user.email || "";
          const name = user.displayName || "";
          const testCode = mapping[quizName];

          await saveTest(userId, testCode, Math.round(score), email, name);

          // Update test results state immediately
          setTestResults(prev => {
            const newMap = new Map(prev);
            newMap.set(testCode, {
              score_percent: Math.round(score),
              completed_at: new Date().toISOString(),
              id: "", // We don't have the document ID yet, but it's ok for UI
            });
            return newMap;
          });
        } catch (error) {
          console.error("Error saving test result:", error);
        }
      };

      saveTestResult();
    }
  }, [isSuccess, quizName, score, setTestResults]);

  return (
    <div className={styles.wrapper}>
      {isSuccess ? <PassedIcon /> : <FailedIcon />}
      <div className={styles.infoWrapper}>
        <Typography variant="h3">{score} %</Typography>

        <Typography variant="h5">
          {isSuccess ? "Congratulations!" : "Almost there!"}
        </Typography>

        <Typography variant="body1" className={styles.body1}>
          {isSuccess
            ? "You passed the test with great results."
            : "You didn’t pass."}
          {!isSuccess && (
            <Button
              variant="text"
              className={styles.button}
              onClick={onResetResults}
            >
              Try again!
            </Button>
          )}
        </Typography>
      </div>

      {isSuccess && (
        <>
          <Button variant="text" onClick={() => setOpen(true)}>
            results
          </Button>
          {handleNextTopic && (
            <Button variant="text" onClick={handleNextTopic}>
              Continue to Next Topic
            </Button>
          )}
        </>
      )}
      {open && (
        <ResultsViewModal
          results={results}
          open={open}
          onClose={() => setOpen(false)}
          quizName={quizName}
        />
      )}
    </div>
  );
};

export default Results;
